/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Box, CircularProgress, Modal, styled } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CustomLabel, CustomSwitch, } from 'components/Common/MUIStyledComponents';
import NotificationsModal from 'components/Monitor/Modals/NotificationModal';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import { enqueueSnackbar } from 'notistack';
import BiteDBAPI from 'api/BiteDBAPI';

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--card-text)',
  },
  'overflow': 'hidden',
  'color': 'var(--Text-Main, var(--text-primary))',
  'textOverflow': 'ellipsis',
  'fontFamily': 'Inter',
  'fontSize': '16px',
  'fontStyle': 'normal',
  'fontWeight': 400,
  'lineHeight': '24px',
  'borderRadius': '8px',
  'border': '1px solid var(--bg-secondary)',
});

const AIDisclaimerBox = styled(Box)({
  backgroundColor: '#2A2D35',
  border: '1px solid #3E424B',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
});

interface DashboardProps {
  database: any;
}

const TariffSettings: React.FC<DashboardProps> = ({ database }) => {
  const [ frequency, setFrequency ] = useState<string>('-');
  const [ weekDay, setWeekDay ] = useState<string>('-');
  const [ riskThreshold, setRiskThreshold ] = useState<string>('Medium');
  const [ selectedLists, setSelectedLists ] = useState<string[]>([]);
  const [ isNotification, setIsNotification ] = useState(false);
  const [ isNotificationOpen, setIsNotificationOpen ] = useState(false);
  const [ isAdvancedSetting, setIsAdvancedSetting ] = useState(false);
  const [ selectedOptsType, setSelectedOptsType ] = useState([]);
  const [ savingDatabases, setSavingDatabases ] = React.useState(false);
  const [ loadingDatabase, setLoadingDatabase ] = React.useState(false);
  const [ databaseData, setDatabaseData ] = React.useState<any>(null);

  // Available screening lists (informational only)
  const availableLists = [
    'EU Restricted Parties',
    'Forced Labor Entities',
    'Worldwide Restricted Parties',
    'US Restricted Parties',
    'Carriers',
  ];

  // Fetch database data on component mount
  useEffect(() => {
    const fetchDatabaseData = async() => {
      if (database?.id) {
        setLoadingDatabase(true);
        try {
          const response = await BiteDBAPI.getDatabaseById(
            database.id.toString(),
          );
          if (response?.success && response?.result?.data) {
            const dbData = response.result.data;
            setDatabaseData(dbData);

            // Populate form fields with database data
            if (dbData.screening_frequency) {
              setFrequency(dbData.screening_frequency);
            }
            if (dbData.screening_day_of_week) {
              setWeekDay(dbData.screening_day_of_week);
            }
            if (dbData.accepted_risk_threshold) {
              setRiskThreshold(dbData.accepted_risk_threshold);
            }
            if (dbData.screening_lists) {
              setSelectedLists(dbData.screening_lists || []);
            } else {
              // Default to all lists if none specified
              setSelectedLists(availableLists);
            }
            if (dbData.notifications_email) {
              const emailArray = dbData.notifications_email
                .split(',')
                .filter((email: string) => email.trim());
              setSelectedOptsType(emailArray);
            }
            if (dbData.notifications_enabled !== undefined) {
              setIsNotification(dbData.notifications_enabled);
            }
          } else {
            enqueueSnackbar('Failed to load database settings', {
              variant: 'error',
            });
          }
        } catch (error) {
          console.error('Error fetching database data:', error);
          enqueueSnackbar('Error loading database settings', {
            variant: 'error',
          });
        } finally {
          setLoadingDatabase(false);
        }
      }
    };

    fetchDatabaseData();
  }, [ database?.id ]);

  useEffect(() => {
    if (selectedOptsType.length > 0) {
      setIsNotification(true);
    } else {
      setIsNotification(false);
    }
  }, [ selectedOptsType ]);

  useEffect(() => {
    const notificationCount = database.notifications_email ?
      database.notifications_email.split(',').filter(Boolean).length :
      0;
    setIsNotification(notificationCount > 0);
    if (database.notifications_email) {
      const opts = database.notifications_email
        .split(',')
        .map((opt: string) => opt.trim())
        .filter(Boolean);

      setSelectedOptsType(opts);
    } else {
      setSelectedOptsType([]); // no notifications
    }
  }, [ database ]);

  const handleFrequencyChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setFrequency(event.target.value as string);
  };

  const handleWeekDayChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setWeekDay(event.target.value as string);
  };

  const handleRiskThresholdChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setRiskThreshold(event.target.value as string);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedOptsType.length > 0) {
      setIsNotificationOpen(true);
    } else {
      // setIsNotification(!isNotification);
      setIsNotificationOpen(!isNotificationOpen);
    }
  };

  const handleSaveSettings = async() => {
    try {
      const ids = [ database.id ];
      setSavingDatabases(true);
      const settingsData = {
        last_updated: Date.now().toString(),
        screening_frequency: frequency,
        screening_day_of_week: weekDay,
        notifications_email: `${selectedOptsType}`,
        notifications_enabled: isNotification,
        accepted_risk_threshold: riskThreshold,
        ids: ids,
      };

      if (!settingsData.ids) {
        enqueueSnackbar('Please select at least one database', {
          variant: 'error',
        });
        return;
      }

      const response = await BiteDBAPI.updateDatabases(settingsData);
      if (response?.success) {
        setSavingDatabases(false);
        enqueueSnackbar('Your schedule setting has been updated successfully', {
          variant: 'success',
        });
      } else {
        setSavingDatabases(false);
        enqueueSnackbar(
          `Error updating schedule settings: ${response?.result}`,
          {
            variant: 'error',
          },
        );
      }
    } catch (e) {
      setSavingDatabases(false);
    }
  };

  return (
    <>
      <div className='screening-settings'>
        {loadingDatabase ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <CircularProgress
              style={{ width: '40px', height: '40px' }}
              color='primary'
            />
          </div>
        ) : (
          <>
            <div className='screening-settings-col' style={{ gap: '0px' }}>
              <h2>
                <SettingsOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                />{' '}
                Database Settings
              </h2>
              <p className='subtitle'>
                Configure automated screening for Tariff Calculations
              </p>
            </div>

            <div className='screening-settings-col'>

              {/* AI Disclaimer */}
              <AIDisclaimerBox>
                <InfoOutlinedIcon
                  sx={{ fontSize: 18, color: '#ffc107', marginTop: '2px' }}
                />
                <div>
                  <div className={'hit-title'}>Tariff Calculation Disclaimer</div>
                  <div
                    className={
                      'audit-add-modal-cont-body-frame-form-row-input-label-text'
                    }
                  >
                    Disclaimer: Users are responsible for manually verifying product scope, effective dates, list numbers, applicable exemptions, and FTAs - the outputs of this tool are an unofficial opinion of the BITE Data Corporation. It is intended to assist in identifying potential tariff implications and should be used alongside a comprehensive compliance review. Reliance upon this advice does not constitute reasonable care under the Customs Modernization Act. In order to exercise the highest degree of reasonable care, BITE Data Corporation recommends that users perform a complete review, which may include application for a binding ruling from U.S. Customs and Border Protection. Should you need further assistance in this matter, please contact us at <a href='mailto:info@bitedata.io'>info@bitedata.io</a>, where we can direct you to additional resources for your trade compliance needs.<br/><br/>
                  View recent and upcoming data updates <a href='https://bitedata.notion.site/'>here</a>
                  </div>
                </div>
              </AIDisclaimerBox>
            </div>

            <div className='screening-divider'></div>

            <div className='screening-settings-col'>
              <div className='hit-title'>
                <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} /> Check
                Frequency
              </div>
              <div className='screening-settings-row'>
                <div
                  className='audit-add-modal-cont-body-frame-form'
                  style={{ width: '100%' }}
                >
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                          Frequency
                        </div>
                        <FormControl sx={{ width: '100%', height: '48px' }}>
                          <CustomSelect
                            value={frequency}
                            onChange={handleFrequencyChange}
                            fullWidth
                            IconComponent={KeyboardArrowDownIcon}
                            inputProps={{
                              MenuProps: {
                                MenuListProps: {
                                  sx: {
                                    backgroundColor: 'var(--bg-colors)',
                                    color: 'var(--Text-Main, var(--text-primary))',
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={'Once'}>Once</MenuItem>
                            <MenuItem value={'Daily'}>Daily</MenuItem>
                            <MenuItem value={'Weekly'}>Weekly</MenuItem>
                            <MenuItem value={'Monthly'}>Monthly</MenuItem>
                            <MenuItem value={'Yearly'}>Yearly</MenuItem>
                          </CustomSelect>
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
                {frequency === 'Weekly' && (
                  <div
                    className='audit-add-modal-cont-body-frame-form'
                    style={{ width: '100%' }}
                  >
                    <div className='audit-add-modal-cont-body-frame-form-row'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                          <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                            Day of Week
                          </div>
                          <FormControl sx={{ width: '100%', height: '48px' }}>
                            <CustomSelect
                              value={weekDay}
                              onChange={handleWeekDayChange}
                              fullWidth
                              IconComponent={KeyboardArrowDownIcon}
                              inputProps={{
                                MenuProps: {
                                  MenuListProps: {
                                    sx: {
                                      backgroundColor: 'var(--bg-colors)',
                                      color: 'var(--Text-Main, var(--text-primary))',
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value={'Monday'}>Monday</MenuItem>
                              <MenuItem value={'Tuesday'}>Tuesday</MenuItem>
                              <MenuItem value={'Wednesday'}>Wednesday</MenuItem>
                              <MenuItem value={'Thursday'}>Thursday</MenuItem>
                              <MenuItem value={'Friday'}>Friday</MenuItem>
                              <MenuItem value={'Saturday'}>Saturday</MenuItem>
                              <MenuItem value={'Sunday'}>Sunday</MenuItem>
                            </CustomSelect>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='screening-divider'></div>

            <div className='screening-settings-col'>
              <div className='hit-main'>
                <div className='hit-title'>
                  <MarkunreadOutlinedIcon sx={{ fontSize: 14 }} /> Email
                  Notifications
                </div>
              </div>
              <div className='screening-settings-row'>
                <div className='hit-main'>
                  <div className='hit-title'>
                    <strong>
                      Send Email Notifications / In-App Notification
                    </strong>
                  </div>
                  <div className='hit-meta'>
                    Get notified when tariff calculation results are available
                  </div>
                </div>
                <CustomLabel
                  control={
                    <CustomSwitch
                      checked={isNotification}
                      onChange={handleSwitchChange}
                      name='saved work'
                    />
                  }
                />
              </div>
            </div>

            <div className='screening-divider'></div>

            <div className='screening-settings-col'>
              <div className='hit-title'>
                <TuneIcon sx={{ fontSize: 14 }} /> Default Values
              </div>

              <div
                className='audit-add-modal-cont-body-frame-form'
                style={{ width: '100%' }}
              >
                <div className='audit-add-modal-cont-body-frame-form-row'>
                  <div className='audit-add-modal-cont-body-frame-form-row-input'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                      <div style={{ fontSize: '14px', color: 'var(--card-text)', marginTop: '8px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: 'var(--text-primary)' }}>These values will be used for transactions if there is no user input for their datapoints:</strong>
                        </div>
                        <div>
                          <strong style={{ color: 'var(--text-primary)' }}>Product Value:</strong> $1,000
                        </div>
                        <div>
                          <strong style={{ color: 'var(--text-primary)' }}>Transportation Method:</strong> Ocean
                        </div>
                        <div>
                          <strong style={{ color: 'var(--text-primary)' }}>Entry and Loading Dates:</strong> Current Date (Date of Uploading Data)
                        </div>
                        <div>
                          <strong style={{ color: 'var(--text-primary)' }}>Country of Pour / Smelt / Loading:</strong> Origin Country Value
                        </div>
                        <div>
                          <strong style={{ color: 'var(--text-primary)' }}>Material Split Amount:</strong> 40% of the Product Value for Line 1
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className='team-content-header'
              style={{ justifyContent: 'flex-end' }}
            >
              <div
                className='create-new-team-btn'
                style={{
                  margin: '0px',
                  border: '1px solid var(--Primary-400, #4895ef)',
                  background: 'var(--Primary-400, #4895ef)',
                }}
                onClick={handleSaveSettings}
              >
                <div
                  className='create-new-team-btn-text'
                  style={{ display: 'flex', color: 'var(--text-primary)' }}
                >
                  {savingDatabases && (
                    <CircularProgress
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '10px',
                      }}
                      color='primary'
                    />
                  )}
                  Save Settings
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <Modal
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      >
        <NotificationsModal
          selectedOptsType={selectedOptsType}
          setSelectedOptsType={setSelectedOptsType}
          handleClose={() => setIsNotificationOpen(false)}
          handleSave={() => setIsNotificationOpen(false)}
        />
      </Modal>
    </>
  );
};

export default TariffSettings;
